/**
 * Every document type carrying a draft stage — which is to say, all of them.
 *
 * The preference acts on its own, with no click behind it, so it is deliberately
 * narrower than the pencil: a sent quote, a confirmed order, an issued invoice or
 * credit note are records other people now read — the customer's copy, a
 * shipment, a ledger entry — and landing on their form by default invites an
 * edit nobody asked for. The pencil stays exactly where it was, for the
 * correction that does need one.
 *
 * A type absent from this list is a type with no draft stage at all (payments,
 * expenses), never one that may be opened at any status.
 */
const DRAFT_ONLY_TYPES = [
    'sales/quotes',
    'sales/orders',
    'purchases/orders',
    'sales/invoices',
    'purchases/invoices',
    'sales/credit-notes',
    'purchases/credit-notes'
];

/**
 * Whether the preference may open this document type in its current status.
 *
 * @param {string} docKey Document type key, e.g. `sales/invoices`
 * @param {?string} status The document's status
 * @return {boolean}
 */
export function autoOpensEditor(docKey, status) {
    return DRAFT_ONLY_TYPES.includes(docKey) ? status === 'draft' : true;
}

/**
 * Which list a record's fiche belongs to, so a user's per-list answer can be
 * found from inside the fiche. Keyed by the `entity-type` every preview already
 * declares, valued with the ListColumnCatalog object type its list is managed
 * under — the same key the column manager panel writes.
 *
 * A fiche whose list is not in the catalog has no panel to set the preference in,
 * so it has no key either: it follows the organization, as it did before.
 *
 * Contacts are the exception the explicit prop exists for: one fiche serves four
 * lists (contacts, customers, suppliers, prospects), and only the route knows
 * which one the user came from.
 */
const GRID_KEY_BY_ENTITY_TYPE = {
    contact: 'contacts',
    task: 'tasks',
    opportunity: 'opportunities',
    project: 'projects',
    work_order: 'work_orders',
    contract: 'contracts'
};

/**
 * The grid key under which this fiche's per-list preferences are stored.
 *
 * @param {?string} entityType The fiche's `entity-type`
 * @param {?string} explicit A key the page states itself, which always wins
 * @return {string} The grid key, or '' when this fiche has no list to read
 */
export function gridKeyForFiche(entityType, explicit = '') {
    return explicit || GRID_KEY_BY_ENTITY_TYPE[entityType] || '';
}

/**
 * Whether records open ready to edit, for one list, for this user.
 *
 * The user's own answer for that list comes first; `'read'` is an answer too, and
 * it has to beat an organization that opens records in edit mode — a preference
 * nobody can turn off for themselves is the one complaint this setting exists to
 * answer. With no answer stored the list follows the organization, so a later
 * change in Settings ▸ Objects still reaches it.
 *
 * @param {Object} args
 * @param {?string} args.userMode This user's mode for the list: 'read', 'edit' or ''
 * @param {boolean} args.orgEnabled Org preference `object_edit_mode_enabled`
 * @return {boolean}
 */
export function editModeEnabledFor({ userMode, orgEnabled }) {
    if (userMode === 'edit') {
        return true;
    }
    if (userMode === 'read') {
        return false;
    }
    return !!orgEnabled;
}

/**
 * Whether a fiche that edits **in place** opens ready to edit — on arrival, and
 * again on every switch to another record inside the same preview.
 *
 * The preference alone decides it — this user's answer for the list it came from,
 * or the organization's when they have none. Switching record is opening a fiche, so
 * it answers the same question as the first one: preference on, the next record
 * opens in edit mode; preference off, it opens in read mode — including when the
 * user had put the previous one in edit mode by hand. Carrying that mode over
 * was the surprise: a pencil clicked on one contact quietly turned every
 * following contact into a form, with the previous record's unsaved changes
 * dropped on the way.
 *
 * @param {Object} args
 * @param {boolean} args.enabled Resolved preference (see `editModeEnabledFor`)
 * @param {boolean} args.editable Whether this user may edit this record now
 * @return {boolean}
 */
export function opensInEditMode({ enabled, editable }) {
    return !!enabled && !!editable;
}

/**
 * Whether opening a record should land on its editor rather than the read-only
 * view — the org preference "Open records in Edit mode" (Objects Manager).
 *
 * Contacts, opportunities and tasks edit in place, so honouring the preference
 * there is a flag BaseOverview flips. Sales and purchases documents do not: they
 * edit on a separate route. Honouring it is then an *action*, and an action must
 * not fire on the ways back **out** of that editor:
 *
 * - Save and Cancel both land on the preview again (`documents/Form.vue`
 *   `handleCancel`), so an unguarded rule would bounce the user straight back
 *   into the form — with no way left to reach the document.
 * - back/forward restored a URL the user picked; the preview *is* the answer.
 * - A preview reached to finish a flow (`?signature=1`, `?approval=1`) opens a
 *   modal of its own; the editor would steal it.
 *
 * `editable` is the same test as the pencil's visibility, never a looser one: we
 * must not navigate anyone to a form their record — locked, paid, superseded —
 * or their abilities would refuse.
 *
 * Kept pure so the decision is testable without a browser; the router and store
 * reads live in the callers.
 *
 * @param {Object} args
 * @param {boolean} args.enabled Resolved preference (see `editModeEnabledFor`)
 * @param {boolean} args.editable Whether this user may edit this record now
 * @param {boolean} args.wasPop Whether the last navigation was back/forward
 * @param {?string} args.fromRoute Name of the route navigated away from
 * @param {string[]} args.editorRoutes Routes that count as "back from the editor"
 * @param {Object} args.query Current route query
 * @param {string[]} args.flowQueries Query keys the preview itself acts on
 * @return {boolean}
 */
export function shouldOpenEditor({
    enabled,
    editable,
    wasPop,
    fromRoute = null,
    editorRoutes = [],
    query = {},
    flowQueries = []
}) {
    if (!enabled || !editable || wasPop) {
        return false;
    }

    if (fromRoute && editorRoutes.includes(fromRoute)) {
        return false;
    }

    return !flowQueries.some(key => query[key] !== undefined);
}
